import React, { useEffect, useMemo, useState, ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import dayjs from 'dayjs';
import { Pencil, RotateCcw, X } from 'lucide-react';
import LetsMeetLayout from 'modules/letsMeet/layouts/LetsMeetLayout';
import { EVENT_ACTIVITIES } from 'utils/constants';
import { Contact } from 'modules/profile/types/types';
import { SquareAutocompleteInput } from 'components/v2/squareAutocompleteInput/SquareAutocompleteInput';
import { SquareMultiSelectInput } from 'components/v2/squareMultiSelectInput/SquareMultiSelectInput';
import EmojiAvatar from 'modules/home/pages/homePage/EmojiAvatar';
import { SquareLocationInput } from 'components/v2/squareLocationInput/SquareLocationInput';
import { Text } from 'components/text/primary/Text';
import Button from 'components/v2/button/Button';
import { Checkbox } from 'components/ui/checkbox';
import {
  useGetEventQuery,
  useUpdateEventMutation,
} from 'services/event.service';
import { IPollData } from 'types/event';
import { ReactComponent as CustomEmojiIcon } from 'assets/images/activity/custom-emoji.svg';
import { useAuth } from 'modules/auth/context/AuthContextV2';
import { useGetAvailableContactsQuery } from 'services/event.service';
import { parseRangeDateString } from 'modules/letsMeet/utils/parseRangeDateString';
import EditEventV2PollGuestsPopup from './EditEventV2PollGuestsPopup';
import EditEventV2LaterTimePopup from './EditEventV2LaterTimePopup';
import CreateEventV2NowTimePopup from '../createEvent/CreateEventV2NowTimePopup';
import { DateTimeRange } from 'modules/letsMeet/types/types';

const EditEventV2: React.FC = () => {
  const { user } = useAuth();
  const [updateEvent, { isLoading }] = useUpdateEventMutation();

  const navigate = useNavigate();

  const { id } = useParams<{
    id: string;
  }>();

  const { data: event, refetch: refetchEvent } = useGetEventQuery(id ?? '');

  const { data: availableContacts } = useGetAvailableContactsQuery();

  const [validationError, setValidationError] = useState<{
    activity?: string | null;
    contacts?: string | null;
    location?: string | null;
    time?: string | null;
  }>({ activity: null, contacts: null, location: null, time: null });

  // activity
  const [customEmoji, setCustomEmoji] = useState<string | null>(null);
  const [selectedActivityId, setSelectedActivityId] = useState<string | null>(
    null,
  );

  // contacts
  const [selectedContactsIds, setSelectedContactsIds] = useState<string[]>([]);
  const [userContacts, setUserContacts] = useState<Contact[]>([]);

  // location
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [selectedLocationName, setSelectedLocationName] = useState<
    string | null
  >(null);

  // now time
  const [isNowVisible, setIsNowVisible] = useState(false);
  const [selectedTimeOption, setSelectedTimeOption] = useState<string | null>(
    null,
  );
  const [selectedDuration, setSelectedDuration] = useState<string | null>(null);

  // later time
  const [isLaterVisible, setIsLaterVisible] = useState(false);
  const [selectedDatesWithTimeRange, setSelectedDatesWithTimeRange] = useState<
    {
      date: Date;
      start: Date | null;
      end: Date | null;
    }[]
  >([]);

  // poll guests
  const [isPollGuestsVisible, setIsPollGuestsVisible] = useState(false);
  const [selectedDatesWithTime, setSelectedDatesWithTime] = useState<
    DateTimeRange[]
  >([]);

  //when combined
  const selectedOptionsToDisplay = useMemo(() => {
    if (selectedTimeOption === 'later') {
      if (selectedDatesWithTimeRange.length === 0) {
        return null;
      }

      return {
        type: 'later',
        dates: selectedDatesWithTimeRange,
      } as const;
    }

    if (selectedDatesWithTime.length === 0) {
      return null;
    }

    return {
      type: 'datesWithTime',
      dates: selectedDatesWithTime,
    } as const;
  }, [selectedTimeOption, selectedDatesWithTime, selectedDatesWithTimeRange]);

  // checkbox state
  const [isPublicEvent, setIsPublicEvent] = useState(false);

  const contactsInputLabel = useMemo(() => {
    if (selectedContactsIds.length === 0) {
      return 'With who?';
    }

    if (selectedContactsIds.length === 1) {
      return 'With 1 friend';
    }

    return `With ${selectedContactsIds.length} friends`;
  }, [selectedContactsIds]);

  const selectedContacts = useMemo(
    () =>
      userContacts.filter((contact) =>
        selectedContactsIds.includes(contact.phoneNumber),
      ),
    [userContacts, selectedContactsIds],
  );

  const formatEventDate = (startDate: Date, endDate: Date) => {
    const format = (date: Date) => dayjs(date).format('YYYY-MM-DD-HH:mm');
    const endTime = (date: Date) => dayjs(date).format('HH:mm');
    return `${format(startDate)}-${endTime(endDate)}`;
  };

  const pollData: IPollData = useMemo(() => {
    return {
      checkedTimes: selectedDatesWithTime.map((time) => ({
        time: formatEventDate(
          time.start,
          time.end ?? dayjs().utc().add(2, 'hours').toDate(),
        ),
        voters: selectedContacts.map((contact) => ({
          name: contact.name,
          userId: contact.userId ?? null,
        })),
      })),
    };
  }, [selectedDatesWithTime, selectedContacts]);

  const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    setValidationError({});

    // Validate only activity
    if (!selectedActivityId) {
      setValidationError((prev) => ({
        ...prev,
        activity: 'Please select an activity.',
      }));
      return;
    }
    console.log(selectedDatesWithTime[0]);
    const newEvents: Parameters<typeof updateEvent>[0][] = [];

    const eventEmoji =
      customEmoji ??
      EVENT_ACTIVITIES.find((activity) => activity.name === selectedActivityId)
        ?.emoji ??
      '📅';

    // If only activity is selected, create event with now type
    if (
      !selectedTimeOption &&
      !selectedLocation &&
      selectedContacts.length === 0
    ) {
      newEvents.push({
        id: event?._id,
        activity: selectedActivityId,
        emoji: eventEmoji,
        location: null,
        contacts: [],
        details: '',
        publicEvent: false,
        type: 'now',
        start: dayjs().toDate(),
        end: dayjs()
          .add(parseInt(selectedDuration ?? '60'), 'minutes')
          .toDate(),
      });
    } else {
      // poll guests
      if (selectedTimeOption === 'poll' && selectedDatesWithTime.length > 0) {
        newEvents.push({
          id: event?._id,
          activity: selectedActivityId,
          emoji: eventEmoji,
          location: selectedLocationName,
          contacts: selectedContacts,
          details: '',
          publicEvent: isPublicEvent,
          type: 'poll',
          start: selectedDatesWithTime[0].start,
          end: selectedDatesWithTime[0].end,
          pollData,
        });
      }
      // later with time
      else if (
        selectedTimeOption === 'later' &&
        selectedDatesWithTimeRange.length > 0
      ) {
        selectedDatesWithTimeRange.forEach((date) => {
          newEvents.push({
            id: event?._id,
            activity: selectedActivityId,
            emoji: eventEmoji,
            location: selectedLocationName,
            contacts: selectedContacts,
            details: '',
            publicEvent: isPublicEvent,
            type: 'later',
            start: date.start,
            end: date.end,
          });
        });
      }
      // now
      else if (selectedTimeOption === 'now') {
        newEvents.push({
          id: event?._id,
          activity: selectedActivityId,
          emoji: eventEmoji,
          location: selectedLocationName,
          contacts: selectedContacts,
          details: '',
          publicEvent: isPublicEvent,
          type: 'now',
          start: dayjs().toDate(),
          end: selectedDuration
            ? dayjs().add(parseInt(selectedDuration), 'minutes').toDate()
            : null,
        });
      }
    }

    const events = await Promise.all(
      newEvents.map(async (newEvent) => {
        try {
          const response = await updateEvent(newEvent);
          const responseId = response.data.message._id;

          return responseId as string;
        } catch (error) {
          console.error('Error creating event:', error);
        }
      }),
    );

    if (events.length !== 0) {
      navigate('/'); // Redirect to homepage after successful update
    }
  };

  useEffect(() => {
    if (user && user.contacts) {
      const sortedAvailableContacts = [...(availableContacts?.data ?? [])].sort(
        (a, b) => a._doc.name.localeCompare(b._doc.name),
      );

      const filteredUserContacts = user.contacts.filter((contact) =>
        sortedAvailableContacts.some(
          (availableContact) =>
            availableContact._doc.phoneNumber !== contact.phoneNumber,
        ),
      );

      const sortedUserContacts = [...filteredUserContacts].sort((a, b) =>
        a.name.localeCompare(b.name),
      );

      setUserContacts([...sortedAvailableContacts, ...sortedUserContacts]);
    }
  }, [user, availableContacts]);

  useEffect(() => {
    if (selectedActivityId) {
      setValidationError((prev) => ({ ...prev, activity: null }));
    }
    if (selectedContactsIds.length > 0 || isPublicEvent) {
      setValidationError((prev) => ({ ...prev, contacts: null }));
    }
    if (selectedLocation) {
      setValidationError((prev) => ({ ...prev, location: null }));
    }
    if (selectedTimeOption) {
      setValidationError((prev) => ({ ...prev, time: null }));
    }
  }, [
    selectedActivityId,
    selectedContactsIds,
    isPublicEvent,
    selectedLocation,
    selectedTimeOption,
  ]);

  const parsedPollDates = useMemo(() => {
    return event?.pollData?.checkedTimes?.map((timeSlot: { time: string }) => {
      return {
        ...timeSlot,
        times: parseRangeDateString(timeSlot.time),
      };
    });
  }, [event]);

  useEffect(() => {
    refetchEvent();

    if (event) {
      if (EVENT_ACTIVITIES.find((activity) => activity.emoji === event.emoji)) {
        setSelectedActivityId(
          EVENT_ACTIVITIES.find((activity) => activity.emoji === event.emoji)
            ?.name || event.activity,
        );
      } else {
        setCustomEmoji(event.emoji);
      }

      setSelectedContactsIds(
        event.contacts
          .filter((contact: any) => contact.organizer === 0)
          .map((contact: any) => contact.phoneNumber),
      );
      setIsPublicEvent(event.publicEvent || false);

      setSelectedLocation(event.location);

      if (event.type === 'poll') {
        const formattedCheckedTimes = parsedPollDates?.map(
          (checkedTime: {
            time: string;
            times: { startDate: Date; endDate: Date };
          }) => ({
            start: dayjs(checkedTime.times.startDate).toDate(),
            end: dayjs(checkedTime.times.endDate).toDate(),
          }),
        );

        const filteredCheckedTimes = formattedCheckedTimes.filter(
          (time: { start: Date; end: Date }) =>
            dayjs(time.start).isValid() && dayjs(time.end).isValid(),
        );

        if (filteredCheckedTimes.length > 0) {
          setSelectedDatesWithTime(filteredCheckedTimes);
          setSelectedTimeOption(event.type || null);
        }
      } else {
        setSelectedTimeOption(event.type || null);
        setSelectedDatesWithTimeRange([
          {
            date: event.start,
            start: event.start,
            end: event.end,
          },
        ]);
      }
    }
  }, [event]);

  return (
    <LetsMeetLayout
      title="Edit Event"
      step="edit"
      footerText={''}
      actionOnHeaderRight={''}
      widget="menu"
      navigateBackUrl="/home"
      bottomChildren={
        <Button onClick={handleSubmit} disabled={isLoading}>
          {isLoading ? 'Saving...' : 'Save'}
        </Button>
      }
    >
      <form className="w-full grid grid-cols-1 gap-6 pt-5 pb-[10px]">
        <SquareAutocompleteInput
          label="What?"
          defaultEmoji={<CustomEmojiIcon className="w-7 h-7" />}
          options={EVENT_ACTIVITIES.map((activity) => ({
            emoji: activity.emoji,
            value: activity.name,
            label: activity.name,
          }))}
          dropdownLabel="CHOOSE ACTIVITY"
          dropdownEmptyLabel="No activities added yet."
          placeholder="Enter activity"
          editInputValue={event?.activity}
          setSelectedOption={setSelectedActivityId}
          selectedOption={selectedActivityId}
          onCustomEmojiChange={setCustomEmoji}
          error={validationError.activity}
          customOption={{
            type: 'emoji',
            label: 'Set custom',
            emoji: customEmoji ?? <CustomEmojiIcon className="w-7 h-7" />,
          }}
        />
        <div className="flex flex-col gap-2">
          <SquareMultiSelectInput
            label={contactsInputLabel}
            options={
              userContacts.map((contact: Contact) => {
                const name = contact.name || contact._doc?.name || '';
                const phoneNumber =
                  contact.phoneNumber || contact._doc?.phoneNumber || '';
                const photoURL = `${process.env.REACT_APP_BASE_URL}/users/profile-pic/${contact.userId || contact._doc?.userId}`;
                const renderAvatar = (size: 'small' | 'list'): ReactNode => (
                  <EmojiAvatar
                    fullName={name}
                    size={size}
                    image={photoURL}
                    isOnline={!!contact._doc?.userId}
                  />
                );

                return {
                  emoji: renderAvatar('list'),
                  getEmoji: (isSelected: boolean) =>
                    renderAvatar(isSelected ? 'small' : 'list'),
                  value: phoneNumber,
                  label: name,
                };
              }) ?? []
            }
            setSelectedOptions={setSelectedContactsIds}
            selectedOptions={selectedContactsIds}
            dropdownLabel="CHOOSE CONTACTS"
            dropdownEmptyLabel="No contacts added yet."
            defaultEmoji="🫂"
            placeholder="Search..."
            disabled={isPublicEvent}
            error={validationError.contacts}
          />
          {selectedContactsIds.length === 0 && (
            <div className="flex items-center gap-2">
              <Checkbox
                checked={isPublicEvent}
                onClick={() => {
                  setIsPublicEvent(!isPublicEvent);
                }}
              />
              <Text
                size="small"
                onClick={() => {
                  setIsPublicEvent(!isPublicEvent);
                }}
              >
                My friends aren&apos;t on Calido
              </Text>
            </div>
          )}
        </div>
        <SquareLocationInput
          selectedLocation={selectedLocation}
          setSelectedLocation={setSelectedLocation}
          setSelectedLocationName={setSelectedLocationName}
          editInputValue={event?.location}
          error={validationError.location}
        />
        <SquareAutocompleteInput
          label="When?"
          defaultEmoji="🕙"
          options={[
            {
              emoji: '❗',
              value: 'now',
              label: 'Now',
              subLabel:
                selectedDuration && selectedTimeOption === 'now'
                  ? `Duration: ${parseInt(selectedDuration) >= 60 ? `${parseInt(selectedDuration) / 60}h` : `${selectedDuration}m`}`
                  : 'Spontaneous hangouts',
              onClick: () => {
                setIsNowVisible(true);
                setSelectedTimeOption('now');
              },
            },
            {
              emoji: '🗓️',
              value: 'later',
              label: 'Later',
              subLabel: 'Choose a specific time',
              onClick: () => {
                setIsLaterVisible(true);
                setSelectedTimeOption('later');
              },
            },
          ]}
          dropdownLabel="CHOOSE TIME"
          placeholder="Set time"
          isDropdownIcon={true}
          readonly={true}
          dropdownEmptyLabel="No time added yet."
          selectedOption={selectedTimeOption}
          setSelectedOption={(newOption) => {
            if (!newOption) {
              setSelectedDuration(null);
            }

            setSelectedTimeOption(newOption);
          }}
          error={validationError.time}
          inputChildren={
            !!selectedOptionsToDisplay && (
              <>
                <div className="flex flex-col gap-2 p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    {selectedOptionsToDisplay.dates.map((option, index) => {
                      const startDate =
                        option instanceof Date ? option : option.start;
                      const endDate =
                        option instanceof Date ? null : option.end;

                      if (!startDate) {
                        return null;
                      }

                      return (
                        <div
                          className="w-[48%] flex justify-between items-center gap-2 border border-solid border-transparent bg-[#32395E] rounded-full pl-4 pr-3 py-2"
                          key={`${startDate}-${endDate}-${index}`}
                        >
                          <div className="flex flex-col">
                            <Text size="small" className="font-manrope">
                              {dayjs(startDate).format('ddd, MMM D')}
                            </Text>
                            <Text
                              size="small"
                              className="text-[#FFFFFF] opacity-[0.6] font-normal font-manrope"
                            >
                              {dayjs(startDate).format('h:mmA')}
                              {endDate &&
                                ` - ${dayjs(endDate).format('h:mmA')}`}
                            </Text>
                          </div>
                          <div
                            className="cursor-pointer"
                            onClick={() => {
                              if (selectedDatesWithTime.length !== 0) {
                                setSelectedDatesWithTime(
                                  selectedDatesWithTime.filter(
                                    (date) => date !== option,
                                  ),
                                );
                              }
                              if (selectedDatesWithTimeRange.length !== 0) {
                                setSelectedDatesWithTimeRange(
                                  selectedDatesWithTimeRange.filter(
                                    (date) => date !== option,
                                  ),
                                );
                              }
                              if (selectedDatesWithTime.length === 0) {
                                setSelectedTimeOption(null);
                              }
                            }}
                          >
                            <X size={16} className="text-[#ABE49C]" />
                          </div>
                        </div>
                      );
                    })}
                    <Pencil
                      size={20}
                      className="cursor-pointer text-[#ABE49C]"
                      onClick={() => {
                        if (selectedTimeOption === 'poll') {
                          setIsPollGuestsVisible(true);
                        } else if (selectedTimeOption === 'later') {
                          setIsLaterVisible(true);
                        }
                      }}
                    />
                  </div>
                  <div
                    className="flex items-center gap-2 pt-2"
                    onClick={() => {
                      setSelectedDatesWithTime([]);
                      setSelectedDatesWithTimeRange([]);
                      setSelectedDuration(null);
                      setSelectedTimeOption(null);
                    }}
                  >
                    <RotateCcw
                      size={20}
                      className="cursor-pointer text-red-500"
                    />
                    <Text size="small" className="cursor-pointer text-red-500">
                      Reset time
                    </Text>
                  </div>
                </div>
              </>
            )
          }
        />
        {selectedTimeOption !== 'now' &&
        selectedDatesWithTime.length === 0 &&
        selectedTimeOption !== 'later' ? (
          <Text size="small" className="flex gap-1">
            Can't decide when?{' '}
            <p
              className="text-[#D7C0FB] underline"
              onClick={() => {
                setIsPollGuestsVisible(true);
                setSelectedTimeOption('poll');
              }}
            >
              Poll your guests
            </p>
          </Text>
        ) : undefined}
      </form>
      <CreateEventV2NowTimePopup
        isOpen={isNowVisible}
        onBack={() => {
          setIsNowVisible(false);

          if (!selectedDuration) {
            setSelectedTimeOption(null);
          }
        }}
        onSave={() => {
          setIsNowVisible(false);
        }}
        selectedDuration={selectedDuration}
        setSelectedDuration={setSelectedDuration}
      />

      <EditEventV2PollGuestsPopup
        isOpen={isPollGuestsVisible}
        onBack={() => {
          setIsPollGuestsVisible(false);

          if (!selectedDatesWithTime.length && !selectedDuration) {
            setSelectedTimeOption(null);
          }
        }}
        onSave={() => {
          setIsPollGuestsVisible(false);
        }}
        selectedDatesWithTime={selectedDatesWithTime}
        setSelectedDatesWithTime={setSelectedDatesWithTime}
        selectedDuration={selectedDuration}
        setSelectedDuration={setSelectedDuration}
      />

      <EditEventV2LaterTimePopup
        isOpen={isLaterVisible}
        onBack={() => {
          setIsLaterVisible(false);

          if (!selectedDatesWithTime.length) {
            setSelectedTimeOption(null);
          }
        }}
        onSave={() => {
          setIsLaterVisible(false);
        }}
        selectedDatesWithTimeRange={selectedDatesWithTimeRange}
        setSelectedDatesWithTimeRange={setSelectedDatesWithTimeRange}
      />
    </LetsMeetLayout>
  );
};

export default EditEventV2;
